import { z } from 'zod';
import { RoleId } from '../members/role.js';
import { AuthDeviceSummary } from './device.js';
import { Locale } from './requests.js';
import { VerificationChallenge } from './verification.js';
export const AuthUser = z.object({
    id: z.string().uuid(),
    // Each identifier is independently nullable. Email/phone self-registration
    // may briefly have no persisted identifier until its challenge is confirmed.
    email: z.string().email().nullable(),
    phone: z.string().nullable(),
    username: z.string().nullable(),
    displayName: z.string(),
    role: RoleId,
    /** Preferred UI language. Drives transactional email language and
     *  in-app i18n default. */
    locale: Locale,
    createdAt: z.string().datetime({ offset: true }),
    // Verification status — null until the user confirms ownership of the
    // channel. Self-registered accounts cannot hold a usable app session until
    // at least one email or phone channel is verified.
    emailVerifiedAt: z.string().datetime({ offset: true }).nullable(),
    phoneVerifiedAt: z.string().datetime({ offset: true }).nullable(),
});
/** Common response for `/auth/login`, `/auth/me`. Carries
 *  `requiresVerification` so the frontend can render a "please verify"
 *  banner without making a second round-trip. The flag is `true` whenever
 *  the user has at least one identifier (email or phone) that has not
 *  been confirmed; admin-created username-only accounts where neither
 *  email nor phone is set are treated as `false`. */
export const AuthSessionResponse = z.object({
    user: AuthUser,
    requiresVerification: z.boolean(),
});
/** Returned by `POST /auth/register`. No app session is created yet; the
 *  `challenge` payload tells the SPA which verification step to render. */
export const AuthRegisterResponse = z.object({
    user: AuthUser,
    challenge: VerificationChallenge,
    requiresVerification: z.literal(true),
});
/** Returned by `POST /auth/me/email/bind/start`. The email remains pending
 *  until the emailed link is consumed, so it does not occupy `users.email`
 *  before ownership is proven. */
export const EmailBindStartResponse = z.object({
    user: AuthUser,
    challenge: VerificationChallenge,
    requiresVerification: z.boolean(),
});
/** Returned by `POST /auth/me/phone/bind/start`. The phone remains pending
 *  until the SMS challenge is confirmed, so it does not occupy `users.phone`
 *  before ownership is proven. */
export const PhoneBindStartResponse = z.object({
    user: AuthUser,
    challenge: VerificationChallenge,
    requiresVerification: z.boolean(),
});
export const RequestPasswordResetResponse = z.object({
    // Always identical regardless of whether the email/phone is registered, to prevent enumeration.
    ok: z.literal(true),
});
export const LogoutResponse = z.object({
    ok: z.literal(true),
});
export const AuthSessionRecord = z.object({
    id: z.string().uuid(),
    current: z.boolean(),
    status: z.enum(['active', 'revoked', 'expired']),
    device: AuthDeviceSummary,
    createdAt: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
    revokedAt: z.string().datetime({ offset: true }).nullable(),
    durationSeconds: z.number().int().nonnegative(),
    remainingSeconds: z.number().int().nonnegative(),
});
export const AuthSecurityEvent = z.object({
    id: z.string().uuid(),
    kind: z.enum([
        'user.registered',
        'user.logged-in',
        'user.logged-out',
        'user.password-reset-requested',
        'user.password-reset-completed',
        'user.email-verified',
        'user.phone-verified',
    ]),
    occurredAt: z.string().datetime({ offset: true }),
    via: z.enum(['email', 'phone', 'username']).nullable(),
    device: AuthDeviceSummary.nullable(),
});
export const AuthSecurityOverviewResponse = z.object({
    sessions: z.array(AuthSessionRecord),
    events: z.array(AuthSecurityEvent),
});
// --- admin -----------------------------------------------------------------
export const EmailDomainListResponse = z.object({
    domains: z.array(z.string()),
});
//# sourceMappingURL=responses.js.map